import { type AxiosError } from 'axios'

import { Bucket } from '~/domains/platform/lib/bucket'

import { type StoneAccountsResponse } from '../entities'
import { getSessionStoneAccounts } from '../services/get-session-stone-accounts'
import { getSessionWeb } from '../services/get-session-web'
import { type Entity, type OptIns, type Subject } from '../types'

import { mergeUnifiedAccounts } from './merge-unified-accounts'
import { sessionWebParser } from './session-web-parser'
import { selectStoneAccount } from './stone-account-selector'

type OneStoneSession = { subject: Subject<OptIns>; entity: Entity<OptIns> }

export const SHOULD_REDIRECT_TO_KYC_KEY = 'session::should::redirect::to::kyc'

export const composeSession = async (
  fetchLegacySession: (activeGlobalId?: string) => Promise<OneStoneSession>
): Promise<OneStoneSession | null> => {
  let stoneAccounts: StoneAccountsResponse

  try {
    stoneAccounts = await getSessionStoneAccounts()
  } catch (error) {
    const errorStatus = (error as AxiosError)?.response?.status

    if (errorStatus === 403 || errorStatus === 404) {
      return fetchLegacySession()
    }

    throw error
  }

  if (stoneAccounts.stoneAccounts.length === 0) {
    if (stoneAccounts.shouldRedirectToKyc) Bucket.session.set(SHOULD_REDIRECT_TO_KYC_KEY, true)
    else Bucket.session.remove(SHOULD_REDIRECT_TO_KYC_KEY)

    return null
  }

  Bucket.session.remove(SHOULD_REDIRECT_TO_KYC_KEY)

  const selectedAccount = selectStoneAccount(stoneAccounts.stoneAccounts)

  if (selectedAccount?.situation === 'one_stone') {
    const sessionWeb = await getSessionWeb({ currentStoneAccountId: selectedAccount.id })
    return sessionWebParser({ sessionWeb, stoneAccounts })
  }

  const legacySession = await fetchLegacySession(selectedAccount?.legacy.globalId)
  return mergeUnifiedAccounts(legacySession, stoneAccounts, selectedAccount)
}
